import { useMemo, useRef } from 'react';
import { buildLoungeAuthHeaders } from './loungeAuthHeader';
import { applyStreamAspectToElement, normalizeStreamSize } from './streamAspectSize';

export const MAX_STREAM_VIDEO_BYTES = 1073741824;
export const MAX_STREAM_VIDEO_DURATION_SECONDS = 120;
export const TUS_THRESHOLD_BYTES = 50 * 1024 * 1024;
export const STREAM_TUS_CHUNK_BYTES = 50 * 1024 * 1024;
export const STREAM_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;
export const STREAM_UPLOAD_STALL_MS = 5 * 60 * 1000;
export const STREAM_VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/*,.mp4,.mov';
const TUS_PATCH_RETRIES = 2;
const MAX_FTYP_BOX_BYTES = 1024;
const MP4_MAJOR_BRANDS = new Set(
  [
    'isom',
    'iso2',
    'iso3',
    'iso4',
    'iso5',
    'iso6',
    'iso8',
    'iso9',
    'mp41',
    'mp42',
    'mp71',
    'avc1',
    'm4v',
    'qt',
    'hev1',
    'hvc1',
    'hevc',
    '3gp4',
    '3gp5',
    '3gp6',
    '3g2a',
    'dash',
  ].map((brand) => brand.padEnd(4, ' ').toLowerCase())
);

export const STREAM_VIDEO_ERRORS = {
  notSignedIn: 'Sign in to upload a video.',
  notMp4: 'Please choose an MP4 or MOV video.',
  tooLarge: 'Videos must be 1 GB or smaller.',
  tooLong: 'Videos must be 2 minutes or shorter.',
  notConfigured: 'Video upload is not configured yet.',
  uploadFailed: 'Sorry, that video could not be uploaded. Please try again.',
  cancelled: 'Upload cancelled.',
  stillProcessing: 'That video is still processing. Please wait a moment and try again.',
  processingFailed: 'Sorry, that video could not be processed. Please try another video.',
  statusUnavailable: 'Sorry, that video could not be uploaded. Please try again.',
  uploadStalled: 'The video upload stalled. Please check your connection and try again.',
} as const;

export const STREAM_READY_POLL_MS = 2000;
export const STREAM_READY_POLL_MAX_MS = 15000;
export const STREAM_READY_MAX_ATTEMPTS = 20;
export const STREAM_READY_TIMEOUT_MS = 180000;
export const STREAM_PROCESSING_PLACEHOLDER_CLASS = 'mw-stream-processing';
export const STREAM_UID_PATTERN = /^[A-Za-z0-9_-]{6,128}$/;

export type StreamUploadProtocol = 'tus' | 'post';

export type MintStreamUploadResult = {
  uploadURL: string;
  uid: string;
  protocol: StreamUploadProtocol;
  iframeSrc: string;
};

export type StreamVideoStatus = {
  uid: string;
  ready: boolean;
  state: string;
  iframeSrc: string;
  width?: number;
  height?: number;
};

export type UploadStreamVideoOptions = {
  onProgress?: (percent: number) => void;
  onMinted?: (uid: string) => void;
  onProcessing?: (status: StreamVideoStatus) => void;
  token?: string | null;
  readDuration?: (file: File) => Promise<number>;
  readFileSize?: (file: File) => Promise<{ width: number; height: number } | null>;
  signal?: AbortSignal;
  readyPollMs?: number;
  readyPollMaxMs?: number;
  readyMaxAttempts?: number;
  readyTimeoutMs?: number;
};

// Same-zone production Worker route. A Lounge overlay without
// REACT_APP_STREAM_UPLOAD_URL (iOS WebView / stale CRA build) used to throw
// "Video upload is not configured yet" for every Advanced Composer user,
// including Club 333, while a later web build with the env set still worked.
// Fall back to the live route; the env still overrides it.
export const DEFAULT_STREAM_UPLOAD_URL = 'https://mousewait.com/lounge/stream-upload';

export function getStreamUploadEndpoint() {
  const configured = String(process.env.REACT_APP_STREAM_UPLOAD_URL || '')
    .trim()
    .replace(/\/+$/, '');
  return configured || DEFAULT_STREAM_UPLOAD_URL;
}

export function getStreamStatusEndpoint() {
  const endpoint = getStreamUploadEndpoint();
  return endpoint ? `${endpoint}/status` : '';
}

export function isStreamUid(value: string) {
  return STREAM_UID_PATTERN.test(String(value || '').trim());
}

// MouseWait's public Stream customer subdomain (it appears in every player URL).
// A build without REACT_APP_STREAM_CUSTOMER_CODE (the #82 deploy build) used to
// distrust every customer-* player URL: the mint threw "could not be uploaded"
// after Stream had already created a 0-byte video, and posted players were
// stripped. Fall back to the production code; the env still overrides it.
export const DEFAULT_STREAM_CUSTOMER_CODE = '2bupgdew4cfp757s';

export function getStreamCustomerCode() {
  return (
    String(process.env.REACT_APP_STREAM_CUSTOMER_CODE || '').trim() ||
    DEFAULT_STREAM_CUSTOMER_CODE
  );
}

export function isAllowedStreamVideoFile(file: File) {
  const name = String(file?.name || '').toLowerCase();
  const type = String(file?.type || '').toLowerCase();
  const allowedExt = name.endsWith('.mp4') || name.endsWith('.mov');
  const allowedType = type === 'video/mp4' || type === 'video/quicktime';
  if (allowedType) return true;
  if (allowedExt && (!type || type === 'application/octet-stream')) return true;
  return false;
}

export function getStreamVideoContentType(file: File) {
  const type = String(file?.type || '').trim().toLowerCase();
  if (type === 'video/mp4' || type === 'video/quicktime') return type;
  const name = String(file?.name || '').toLowerCase();
  if (name.endsWith('.mov')) return 'video/quicktime';
  return 'video/mp4';
}

export function getStreamVideoFileError(file: File | null | undefined): string | null {
  if (!file) return STREAM_VIDEO_ERRORS.notMp4;
  if (!isAllowedStreamVideoFile(file)) return STREAM_VIDEO_ERRORS.notMp4;
  if (file.size > MAX_STREAM_VIDEO_BYTES) return STREAM_VIDEO_ERRORS.tooLarge;
  return null;
}

export function getStreamVideoDurationError(durationSeconds: number): string | null {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return STREAM_VIDEO_ERRORS.uploadFailed;
  }
  if (durationSeconds > MAX_STREAM_VIDEO_DURATION_SECONDS) {
    return STREAM_VIDEO_ERRORS.tooLong;
  }
  return null;
}

export function chooseStreamUploadProtocol(size: number): StreamUploadProtocol {
  return size > TUS_THRESHOLD_BYTES ? 'tus' : 'post';
}

function readBlobBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error || new Error('Could not read file'));
    reader.readAsArrayBuffer(blob);
  });
}

function readFourCc(bytes: Uint8Array, offset: number) {
  return String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3]);
}

export async function hasMp4FtypBox(file: File): Promise<boolean> {
  if (!file || file.size < 16 || typeof file.slice !== 'function') return false;
  try {
    const prefix = await readBlobBytes(file.slice(0, 16));
    if (prefix.length < 16) return false;
    const size = ((prefix[0] << 24) | (prefix[1] << 16) | (prefix[2] << 8) | prefix[3]) >>> 0;
    if (size < 16 || size > file.size || size > MAX_FTYP_BOX_BYTES) {
      return false;
    }
    if (readFourCc(prefix, 4) !== 'ftyp') {
      return false;
    }
    const brand = readFourCc(prefix, 8).toLowerCase();
    return MP4_MAJOR_BRANDS.has(brand);
  } catch {
    return false;
  }
}

export function buildStreamPlayerSrc(uid: string, customerCode?: string) {
  const safeUid = encodeURIComponent(String(uid || '').trim());
  const code = String(customerCode || getStreamCustomerCode() || '').trim();
  if (code) {
    return `https://customer-${code}.cloudflarestream.com/${safeUid}/iframe?playsinline=true`;
  }
  return `https://iframe.videodelivery.net/${safeUid}?playsinline=true`;
}

// Stream's default thumbnail is frame 0, which is black for most phone videos,
// so the feed showed an empty player until Play. Every player carries an
// explicit poster; 1 s is the default frame when none was chosen.
export const DEFAULT_STREAM_POSTER_SECONDS = 1;

export function clampStreamPosterSeconds(seconds: number, durationSeconds?: number) {
  const value = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const max =
    Number.isFinite(durationSeconds) && Number(durationSeconds) > 0
      ? Math.max(0, Number(durationSeconds) - 0.1)
      : Infinity;
  return Math.round(Math.min(value, max) * 10) / 10;
}

export function defaultStreamPosterSeconds(durationSeconds?: number) {
  if (Number.isFinite(durationSeconds) && Number(durationSeconds) > 0) {
    return clampStreamPosterSeconds(
      Math.min(DEFAULT_STREAM_POSTER_SECONDS, Number(durationSeconds) / 2),
      durationSeconds
    );
  }
  return DEFAULT_STREAM_POSTER_SECONDS;
}

export function buildStreamThumbnailUrl(
  uid: string,
  seconds: number,
  options: { height?: number; customerCode?: string } = {}
) {
  const safeUid = encodeURIComponent(String(uid || '').trim());
  const code = String(options.customerCode || getStreamCustomerCode() || '').trim();
  const base = code
    ? `https://customer-${code}.cloudflarestream.com/${safeUid}/thumbnails/thumbnail.jpg`
    : `https://videodelivery.net/${safeUid}/thumbnails/thumbnail.jpg`;
  const params = new URLSearchParams({ time: `${clampStreamPosterSeconds(seconds)}s` });
  if (options.height) params.set('height', String(options.height));
  return `${base}?${params.toString()}`;
}

export function readStreamPosterSeconds(src: string): number | null {
  try {
    const poster = new URL(src, 'https://mousewait.com').searchParams.get('poster');
    if (!poster) return null;
    const time = new URL(poster).searchParams.get('time') || '';
    const match = /^(\d+(?:\.\d+)?)s?$/.exec(time.trim());
    return match ? Number(match[1]) : null;
  } catch {
    return null;
  }
}

export function withStreamPoster(src: string, seconds: number) {
  try {
    const url = new URL(src);
    const uid = url.pathname.split('/').filter(Boolean)[0] || '';
    if (!isStreamUid(uid)) return src;
    const host = url.hostname.toLowerCase();
    const code = /^customer-([a-z0-9]+)\.cloudflarestream\.com$/.exec(host)?.[1];
    url.searchParams.set('poster', buildStreamThumbnailUrl(uid, seconds, { customerCode: code }));
    return url.toString();
  } catch {
    return src;
  }
}

export const STREAM_ASPECT_PARAM = 'mw_aspect';

export function readMwAspectFromSrc(src: string): { width: number; height: number } | null {
  try {
    const raw = new URL(src, 'https://mousewait.com').searchParams.get(STREAM_ASPECT_PARAM) || '';
    const match = /^(\d+)x(\d+)$/i.exec(raw.trim());
    if (!match) return null;
    return normalizeStreamSize(Number(match[1]), Number(match[2]));
  } catch {
    return null;
  }
}

export function withStreamAspect(src: string, width?: number, height?: number) {
  const size = normalizeStreamSize(Number(width), Number(height));
  if (!size) return src;
  try {
    const url = new URL(src);
    url.searchParams.set(STREAM_ASPECT_PARAM, `${size.width}x${size.height}`);
    return url.toString();
  } catch {
    return src;
  }
}

function escapeHtmlAttr(value: string) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function buildStreamPlayerIframe(srcOrUid: string) {
  const src = /:\/\//.test(srcOrUid)
    ? srcOrUid
    : buildStreamPlayerSrc(srcOrUid);
  const safeSrc = escapeHtmlAttr(src);
  return `<iframe class="ql-video mw-stream-video" src="${safeSrc}" playsinline="true" webkit-playsinline="true" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen="true"></iframe>`;
}

export function isStreamPlayerHost(url: string) {
  try {
    const parsed = new URL(url, 'https://mousewait.com');
    if (parsed.protocol !== 'https:') return false;
    const host = parsed.hostname.toLowerCase();
    if (host === 'iframe.videodelivery.net') return true;
    const expected = getStreamCustomerCode();
    if (!expected) return false;
    return host === `customer-${expected.toLowerCase()}.cloudflarestream.com`;
  } catch {
    return false;
  }
}

export async function readVideoFileMeta(
  file: File
): Promise<{ duration: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    const objectUrl = URL.createObjectURL(file);
    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      video.removeAttribute('src');
      video.load();
    };
    video.onloadedmetadata = () => {
      const duration = video.duration;
      const width = video.videoWidth;
      const height = video.videoHeight;
      cleanup();
      resolve({ duration, width, height });
    };
    video.onerror = () => {
      cleanup();
      reject(new Error(STREAM_VIDEO_ERRORS.uploadFailed));
    };
    video.src = objectUrl;
  });
}

export async function readVideoDurationSeconds(file: File): Promise<number> {
  const meta = await readVideoFileMeta(file);
  return meta.duration;
}

export async function mintStreamUploadUrl({
  token,
  size,
  filename,
  contentType,
  protocol = 'post',
  signal,
}: {
  token: string;
  size: number;
  filename: string;
  contentType: string;
  protocol?: StreamUploadProtocol;
  signal?: AbortSignal;
}): Promise<MintStreamUploadResult> {
  const endpoint = getStreamUploadEndpoint();
  if (!endpoint) {
    throw new Error(STREAM_VIDEO_ERRORS.notConfigured);
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      ...buildLoungeAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ size, filename, contentType, protocol }),
    signal,
  });
  const result = await response.json().catch(() => null);
  if (
    response.ok &&
    result?.uploadURL &&
    result?.uid &&
    (result.protocol === 'tus' || result.protocol === 'post')
  ) {
    const workerIframe =
      typeof result.iframeSrc === 'string' && result.iframeSrc ? result.iframeSrc : '';
    // Never trust an unexpected host, but do not fail the upload over it:
    // Stream has already created the video, so rebuild the player URL from the uid.
    const iframeSrc =
      workerIframe && isStreamPlayerHost(workerIframe)
        ? workerIframe
        : buildStreamPlayerSrc(result.uid);
    return {
      uploadURL: result.uploadURL,
      uid: result.uid,
      protocol: result.protocol,
      iframeSrc,
    };
  }

  const message =
    typeof result?.error === 'string' ? result.error : STREAM_VIDEO_ERRORS.uploadFailed;
  throw new Error(message);
}

function assertNever(value: never, message: string): never {
  throw new Error(message);
}

function applyMwAspectFromSrc(el: HTMLElement, src: string) {
  const size = readMwAspectFromSrc(src);
  if (!size) return;
  applyStreamAspectToElement(el, size.width, size.height);
}

function applyEditorStreamAspects(editor: any) {
  const root = editor?.root;
  if (!root?.querySelectorAll) return;
  Array.from(root.querySelectorAll('iframe') as NodeListOf<HTMLIFrameElement>).forEach((iframe) => {
    const src = iframe.getAttribute('src') || '';
    if (isStreamPlayerHost(src)) applyMwAspectFromSrc(iframe, src);
  });
}

export function insertStreamPlayerIntoEditor(
  editor: any,
  iframeSrc: string,
  syncHtml?: (html: string) => void
) {
  if (!editor || !iframeSrc || !isStreamPlayerHost(iframeSrc)) return;
  const range = editor.getSelection?.(false);
  const cursor = range?.index ?? editor.getLength?.() ?? 0;
  editor.insertEmbed(cursor, 'video', iframeSrc, 'user');
  editor.setSelection?.(cursor + 1, 0, 'silent');
  applyEditorStreamAspects(editor);
  const html = editor.root?.innerHTML;
  if (typeof html === 'string') {
    syncHtml?.(html);
  }
}

export function attachStreamPlayerHtml(
  editor: any,
  iframeSrc: string,
  currentHtml: string,
  syncHtml: (html: string) => void
) {
  if (!iframeSrc || !isStreamPlayerHost(iframeSrc)) return;
  if (editor) {
    insertStreamPlayerIntoEditor(editor, iframeSrc, syncHtml);
    return;
  }
  const iframe = buildStreamPlayerIframe(iframeSrc);
  const html = String(currentHtml || '');
  syncHtml(html.includes(iframeSrc) ? html : `${html}${iframe}`);
}

export function buildStreamProcessingPlaceholder(uid: string) {
  const safeUid = escapeHtmlAttr(String(uid || '').trim());
  return `<p class="${STREAM_PROCESSING_PLACEHOLDER_CLASS}" data-stream-uid="${safeUid}" role="status">Processing video…</p>`;
}

export function hasStreamProcessingPlaceholder(html: string) {
  const value = String(html || '');
  return (
    value.includes(`class="${STREAM_PROCESSING_PLACEHOLDER_CLASS}"`) ||
    value.includes(`class='${STREAM_PROCESSING_PLACEHOLDER_CLASS}'`) ||
    /data-stream-uid=/.test(value)
  );
}

export function getStreamComposeBlockReason(busy: boolean, html?: string) {
  if (busy) return STREAM_VIDEO_ERRORS.stillProcessing;
  if (hasStreamProcessingPlaceholder(String(html || ''))) {
    return STREAM_VIDEO_ERRORS.stillProcessing;
  }
  return null;
}

export function nextStreamReadyPollDelay(
  currentMs: number,
  maxMs = STREAM_READY_POLL_MAX_MS
) {
  const current = Number.isFinite(currentMs) && currentMs > 0 ? currentMs : STREAM_READY_POLL_MS;
  return Math.min(Math.max(1, Math.round(current * 1.5)), maxMs);
}

function streamPlaceholderPattern(uid?: string) {
  if (uid && isStreamUid(uid)) {
    const escaped = escapeHtmlAttr(uid).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(
      `<p[^>]*data-stream-uid="${escaped}"[^>]*>[\\s\\S]*?<\\/p>`,
      'i'
    );
  }
  return new RegExp(
    `<p[^>]*class="[^"]*${STREAM_PROCESSING_PLACEHOLDER_CLASS}[^"]*"[^>]*>[\\s\\S]*?<\\/p>`,
    'i'
  );
}

function stripTrustedStreamIframes(html: string) {
  return String(html || '').replace(/<iframe\b[^>]*>\s*<\/iframe>/gi, (tag) => {
    const src = /src=["']([^"']+)["']/i.exec(tag)?.[1] || '';
    return isStreamPlayerHost(src) ? '' : tag;
  });
}

export function stripStreamEditorMarkup(html: string, uid?: string) {
  let next = String(html || '');
  next = next.replace(streamPlaceholderPattern(uid), '');
  if (uid) {
    next = next.replace(streamPlaceholderPattern(), '');
  }
  return stripTrustedStreamIframes(next);
}

function quillFind(editor: any, node: Node) {
  if (typeof editor?.constructor?.find === 'function') {
    return editor.constructor.find(node);
  }
  if (typeof editor?.scroll?.find === 'function') {
    return editor.scroll.find(node);
  }
  return null;
}

function quillIndexOf(editor: any, node: Node): number | null {
  const blot = quillFind(editor, node);
  if (!blot) return null;
  const index = typeof blot.offset === 'function' ? blot.offset(editor.scroll) : blot.offset;
  return Number.isFinite(index) ? Number(index) : null;
}

function quillDeleteNode(editor: any, node: Node) {
  const blot = quillFind(editor, node);
  const index = quillIndexOf(editor, node);
  if (index == null || typeof editor.deleteText !== 'function') return false;
  const length = typeof blot?.length === 'function' ? blot.length() : 1;
  editor.deleteText(index, Math.max(1, length), 'user');
  return true;
}

function collectStreamNodes(editor: any, uid?: string): Element[] {
  const root = editor?.root;
  if (!root?.querySelectorAll) return [];
  const safeUid = uid && isStreamUid(uid) ? escapeHtmlAttr(uid) : '';
  const placeholderSelector = safeUid
    ? `.${STREAM_PROCESSING_PLACEHOLDER_CLASS}[data-stream-uid="${safeUid}"], [data-stream-uid="${safeUid}"]`
    : `.${STREAM_PROCESSING_PLACEHOLDER_CLASS}, [data-stream-uid]`;
  const placeholders = Array.from(root.querySelectorAll(placeholderSelector)) as Element[];
  const iframes = (Array.from(root.querySelectorAll('iframe')) as Element[]).filter((node) =>
    isStreamPlayerHost(node.getAttribute('src') || '')
  );
  return [...placeholders, ...iframes];
}

function readEditorHtml(editor: any, fallback: string) {
  return typeof editor?.root?.innerHTML === 'string' ? editor.root.innerHTML : fallback;
}

const STREAM_PROCESSING_TEXT = 'Processing video…';

// Real Quill drops the placeholder's class/data attributes on paste, leaving
// plain "Processing video…" text that DOM lookups can't find. Track it by text.
function findPlaceholderTextIndexes(editor: any): number[] {
  if (typeof editor?.getText !== 'function') return [];
  const text = String(editor.getText() || '');
  const found: number[] = [];
  let at = text.indexOf(STREAM_PROCESSING_TEXT);
  while (at >= 0) {
    found.push(at);
    at = text.indexOf(STREAM_PROCESSING_TEXT, at + STREAM_PROCESSING_TEXT.length);
  }
  return found;
}

function deletePlaceholderText(editor: any) {
  if (typeof editor?.deleteText !== 'function') return null;
  const indexes = findPlaceholderTextIndexes(editor);
  indexes
    .slice()
    .reverse()
    .forEach((index) => editor.deleteText(index, STREAM_PROCESSING_TEXT.length, 'user'));
  return indexes.length ? indexes[0] : null;
}

export function removeStreamEditorMarkup(
  editor: any,
  uid: string | undefined,
  currentHtml: string,
  syncHtml: (html: string) => void
) {
  if (editor && typeof editor.deleteText === 'function' && editor.root) {
    const ranked = collectStreamNodes(editor, uid)
      .map((node) => ({ node, index: quillIndexOf(editor, node) ?? -1 }))
      .filter((item) => item.index >= 0)
      .sort((a, b) => b.index - a.index);
    ranked.forEach((item) => quillDeleteNode(editor, item.node));
    deletePlaceholderText(editor);
    if (collectStreamNodes(editor, uid).length) {
      syncHtml(stripStreamEditorMarkup(readEditorHtml(editor, currentHtml), uid));
      return;
    }
    syncHtml(readEditorHtml(editor, currentHtml));
    return;
  }
  syncHtml(stripStreamEditorMarkup(currentHtml, uid));
}

export function attachStreamProcessingPlaceholder(
  editor: any,
  uid: string,
  currentHtml: string,
  syncHtml: (html: string) => void
) {
  if (!isStreamUid(uid)) return;
  const placeholder = buildStreamProcessingPlaceholder(uid);
  if (editor && (typeof editor.insertText === 'function' || editor.clipboard?.dangerouslyPasteHTML)) {
    const existingAt = findPlaceholderTextIndexes(editor)[0] ?? null;
    removeStreamEditorMarkup(editor, undefined, currentHtml, () => undefined);
    const index =
      existingAt ??
      editor.getSelection?.(false)?.index ??
      Math.max(0, (editor.getLength?.() ?? 1) - 1);
    if (typeof editor.clipboard?.dangerouslyPasteHTML === 'function') {
      editor.clipboard.dangerouslyPasteHTML(index, placeholder, 'user');
    } else {
      editor.insertText(index, STREAM_PROCESSING_TEXT, 'user');
    }
    syncHtml(readEditorHtml(editor, `${currentHtml}${placeholder}`));
    return;
  }
  syncHtml(`${stripStreamEditorMarkup(currentHtml, uid)}${placeholder}`);
}

export function replaceStreamProcessingPlaceholder(
  editor: any,
  uid: string | undefined,
  iframeSrc: string,
  currentHtml: string,
  syncHtml: (html: string) => void
) {
  if (!iframeSrc || !isStreamPlayerHost(iframeSrc)) return;
  if (editor && typeof editor.insertEmbed === 'function') {
    const placeholderNode = editor.root?.querySelector?.(
      uid && isStreamUid(uid)
        ? `[data-stream-uid="${escapeHtmlAttr(uid)}"]`
        : `.${STREAM_PROCESSING_PLACEHOLDER_CLASS}`
    ) as Element | null;
    const insertAt = placeholderNode ? quillIndexOf(editor, placeholderNode) : null;
    const textAt = findPlaceholderTextIndexes(editor)[0] ?? null;
    removeStreamEditorMarkup(editor, uid, currentHtml, () => undefined);
    const index =
      insertAt ??
      textAt ??
      editor.getSelection?.(false)?.index ??
      Math.max(0, (editor.getLength?.() ?? 1) - 1);
    editor.insertEmbed(index, 'video', iframeSrc, 'user');
    editor.setSelection?.(index + 1, 0, 'silent');
    applyEditorStreamAspects(editor);
    syncHtml(readEditorHtml(editor, currentHtml));
    return;
  }
  syncHtml(`${stripStreamEditorMarkup(currentHtml, uid)}${buildStreamPlayerIframe(iframeSrc)}`);
}

export type StreamEditorPlayerSnapshot = {
  uid: string;
  iframeSrc: string;
};

export function readUidFromStreamPlayerSrc(src: string) {
  try {
    const parsed = new URL(src, 'https://mousewait.com');
    const uid = parsed.pathname.split('/').filter(Boolean)[0] || '';
    return isStreamUid(uid) ? uid : '';
  } catch {
    return '';
  }
}

export function readStreamEditorPlayerSnapshot(
  editor: any,
  html: string
): StreamEditorPlayerSnapshot | null {
  const srcFromEditor = String(editor?.root?.querySelector?.('iframe')?.getAttribute?.('src') || '');
  const src =
    srcFromEditor && isStreamPlayerHost(srcFromEditor)
      ? srcFromEditor
      : /<iframe\b[^>]*src=["']([^"']+)["']/i.exec(String(html || ''))?.[1] || '';
  if (!src || !isStreamPlayerHost(src)) return null;
  return {
    uid: readUidFromStreamPlayerSrc(src),
    iframeSrc: src,
  };
}

export function restoreStreamEditorPlayer(
  editor: any,
  snapshot: StreamEditorPlayerSnapshot | null,
  currentHtml: string,
  syncHtml: (html: string) => void
) {
  if (snapshot?.iframeSrc && isStreamPlayerHost(snapshot.iframeSrc)) {
    replaceStreamProcessingPlaceholder(editor, snapshot.uid, snapshot.iframeSrc, currentHtml, syncHtml);
    return;
  }
  removeStreamEditorMarkup(editor, snapshot?.uid, currentHtml, syncHtml);
}

export function setStreamEditorPoster(
  editor: any,
  uid: string,
  seconds: number,
  currentHtml: string,
  syncHtml: (html: string) => void
) {
  if (!isStreamUid(uid)) return;
  const node = (Array.from(editor?.root?.querySelectorAll?.('iframe') || []) as Element[]).find(
    (iframe) => {
      const src = iframe.getAttribute('src') || '';
      return isStreamPlayerHost(src) && readUidFromStreamPlayerSrc(src) === uid;
    }
  );
  if (node && typeof editor.insertEmbed === 'function') {
    const nextSrc = withStreamPoster(node.getAttribute('src') || '', seconds);
    if (nextSrc === node.getAttribute('src')) return;
    const index = quillIndexOf(editor, node);
    if (index != null && typeof editor.deleteText === 'function') {
      editor.deleteText(index, 1, 'user');
      editor.insertEmbed(index, 'video', nextSrc, 'user');
    } else {
      node.setAttribute('src', nextSrc);
    }
    applyEditorStreamAspects(editor);
    syncHtml(readEditorHtml(editor, currentHtml));
    return;
  }
  const html = String(currentHtml || '');
  const next = html.replace(/<iframe\b[^>]*>/gi, (tag) =>
    tag.replace(/src=(["'])([^"']+)\1/i, (attr, quote, rawSrc) => {
      const src = rawSrc.replace(/&amp;/g, '&');
      if (!isStreamPlayerHost(src) || readUidFromStreamPlayerSrc(src) !== uid) return attr;
      return `src=${quote}${escapeHtmlAttr(withStreamPoster(src, seconds))}${quote}`;
    })
  );
  if (next !== html) syncHtml(next);
}

export type StreamUploadEditorSnapshot = {
  prior: StreamEditorPlayerSnapshot | null;
  strippedPrior: boolean;
};

export function createStreamUploadEditorSnapshot(): StreamUploadEditorSnapshot {
  return { prior: null, strippedPrior: false };
}

export function bindStreamUploadToEditor(
  getEditor: () => any,
  getHtml: () => string,
  syncHtml: (html: string) => void,
  snapshot: StreamUploadEditorSnapshot = createStreamUploadEditorSnapshot()
) {
  return {
    onProcessing(status: StreamVideoStatus) {
      if (!snapshot.strippedPrior) {
        snapshot.prior = readStreamEditorPlayerSnapshot(getEditor(), getHtml());
        snapshot.strippedPrior = true;
      }
      attachStreamProcessingPlaceholder(getEditor(), status.uid, getHtml(), syncHtml);
    },
    onInsert(iframeSrc: string, uid?: string) {
      replaceStreamProcessingPlaceholder(getEditor(), uid, iframeSrc, getHtml(), syncHtml);
      snapshot.strippedPrior = false;
    },
    onRemove(uid?: string) {
      removeStreamEditorMarkup(getEditor(), uid, getHtml(), syncHtml);
      snapshot.prior = null;
      snapshot.strippedPrior = false;
    },
    onPosterChange(uid: string, seconds: number) {
      setStreamEditorPoster(getEditor(), uid, seconds, getHtml(), syncHtml);
    },
    onRestore() {
      if (!snapshot.strippedPrior) return;
      restoreStreamEditorPlayer(getEditor(), snapshot.prior, getHtml(), syncHtml);
      snapshot.strippedPrior = false;
    },
  };
}

export function useBindStreamUploadToEditor(
  getEditor: () => any,
  getHtml: () => string,
  syncHtml: (html: string) => void
) {
  const snapshotRef = useRef(createStreamUploadEditorSnapshot());
  const gettersRef = useRef({ getEditor, getHtml, syncHtml });
  gettersRef.current = { getEditor, getHtml, syncHtml };
  return useMemo(
    () =>
      bindStreamUploadToEditor(
        () => gettersRef.current.getEditor(),
        () => gettersRef.current.getHtml(),
        (html) => gettersRef.current.syncHtml(html),
        snapshotRef.current
      ),
    []
  );
}

export async function fetchStreamVideoStatus({
  uid,
  token,
  signal,
}: {
  uid: string;
  token: string;
  signal?: AbortSignal;
}): Promise<StreamVideoStatus> {
  const endpoint = getStreamStatusEndpoint();
  if (!endpoint) {
    throw new Error(STREAM_VIDEO_ERRORS.notConfigured);
  }
  if (!isStreamUid(uid)) {
    throw new Error(STREAM_VIDEO_ERRORS.uploadFailed);
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      ...buildLoungeAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ uid }),
    signal,
  });
  const result = await response.json().catch(() => null);
  const iframeSrc =
    typeof result?.iframeSrc === 'string' && result.iframeSrc
      ? result.iframeSrc
      : buildStreamPlayerSrc(uid);
  const trustedIframe = isStreamPlayerHost(iframeSrc)
    ? iframeSrc
    : buildStreamPlayerSrc(uid);
  const state = typeof result?.state === 'string' && result.state ? result.state : 'unknown';
  const width = Number(result?.width);
  const height = Number(result?.height);
  const sizedIframe = withStreamAspect(trustedIframe, width, height);
  const sized =
    Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
      ? { width: Math.round(width), height: Math.round(height) }
      : {};
  if (state === 'error' || result?.error === STREAM_VIDEO_ERRORS.processingFailed) {
    throw new Error(STREAM_VIDEO_ERRORS.processingFailed);
  }
  if (response.status === 401) {
    throw new Error(STREAM_VIDEO_ERRORS.notSignedIn);
  }
  if (response.status === 429) {
    return {
      uid,
      ready: false,
      state: 'limited',
      iframeSrc: sizedIframe,
      ...sized,
    };
  }
  const isStreamNotFound =
    state === 'notfound' ||
    state === 'pendingupload' ||
    (response.status === 404 && typeof result?.uid === 'string') ||
    (response.status === 404 && result?.ready === false);
  // Stream can 404 for a few seconds after TUS bytes land, and a mint that
  // omitted Upload-Creator looks like notfound. Keep polling — never treat
  // that ownership 404 as an upload failure.
  if (isStreamNotFound) {
    return {
      uid,
      ready: false,
      state: 'pendingupload',
      iframeSrc: sizedIframe,
      ...sized,
    };
  }
  // Bare Worker 404 `{ error: 'Not found.' }` means /status is not routed.
  // Keep the upload (TUS already landed); the composer treats this as
  // still processing rather than a hard failure.
  if (response.status === 404) {
    return {
      uid,
      ready: false,
      state: 'pendingupload',
      iframeSrc: sizedIframe,
      ...sized,
    };
  }
  if (
    response.ok &&
    result?.uid &&
    typeof result.ready === 'boolean' &&
    isStreamPlayerHost(trustedIframe)
  ) {
    return {
      uid: String(result.uid),
      ready: result.ready === true,
      state,
      iframeSrc: sizedIframe,
      ...sized,
    };
  }

  const message =
    typeof result?.error === 'string' ? result.error : STREAM_VIDEO_ERRORS.uploadFailed;
  throw new Error(message);
}

function sleep(ms: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error(STREAM_VIDEO_ERRORS.cancelled));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new Error(STREAM_VIDEO_ERRORS.cancelled));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export async function waitForStreamVideoReady({
  uid,
  token,
  signal,
  onStatus,
  pollMs = STREAM_READY_POLL_MS,
  pollMaxMs = STREAM_READY_POLL_MAX_MS,
  maxAttempts = STREAM_READY_MAX_ATTEMPTS,
  timeoutMs = STREAM_READY_TIMEOUT_MS,
}: {
  uid: string;
  token: string;
  signal?: AbortSignal;
  onStatus?: (status: StreamVideoStatus) => void;
  pollMs?: number;
  pollMaxMs?: number;
  maxAttempts?: number;
  timeoutMs?: number;
}): Promise<StreamVideoStatus> {
  const started = Date.now();
  let delay = Math.max(1, pollMs);
  let last: StreamVideoStatus | null = null;
  const attemptsCap = Math.max(1, maxAttempts);
  for (let attempt = 1; attempt <= attemptsCap; attempt += 1) {
    throwIfAborted(signal);
    if (Date.now() - started > timeoutMs) {
      break;
    }
    const status = await fetchStreamVideoStatus({ uid, token, signal });
    last = status;
    onStatus?.(status);
    if (status.ready) {
      return status;
    }
    if (status.state === 'error') {
      throw new Error(STREAM_VIDEO_ERRORS.processingFailed);
    }
    if (attempt === attemptsCap) {
      break;
    }
    await sleep(delay, signal);
    delay = nextStreamReadyPollDelay(delay, pollMaxMs);
  }
  if (last?.ready) {
    return last;
  }
  throw new Error(STREAM_VIDEO_ERRORS.stillProcessing);
}

function isAbortError(error: unknown) {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && /cancel/i.test(error.message))
  );
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) {
    throw new Error(STREAM_VIDEO_ERRORS.cancelled);
  }
}

function xhrSend({
  method,
  url,
  headers,
  body,
  onProgress,
  signal,
  timeoutMs = STREAM_UPLOAD_TIMEOUT_MS,
  stallMs = STREAM_UPLOAD_STALL_MS,
}: {
  method: string;
  url: string;
  headers?: Record<string, string>;
  body?: Blob | File | null;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
  timeoutMs?: number;
  stallMs?: number;
}): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    const clearWatchdogs = () => {
      signal?.removeEventListener('abort', failCancelled);
      if (stallTimer !== undefined) {
        clearTimeout(stallTimer);
        stallTimer = undefined;
      }
    };
    const settle = (finish: () => void) => {
      if (settled) return;
      settled = true;
      clearWatchdogs();
      finish();
    };
    const failCancelled = () => {
      settle(() => {
        xhr.abort();
        reject(new Error(STREAM_VIDEO_ERRORS.cancelled));
      });
    };
    const failStalled = () => {
      settle(() => {
        xhr.abort();
        reject(new Error(STREAM_VIDEO_ERRORS.uploadStalled));
      });
    };
    const armStall = () => {
      if (!stallMs || stallMs <= 0) return;
      if (stallTimer !== undefined) clearTimeout(stallTimer);
      stallTimer = setTimeout(failStalled, stallMs);
    };
    if (signal?.aborted) {
      failCancelled();
      return;
    }
    signal?.addEventListener('abort', failCancelled);
    xhr.open(method, url);
    if (timeoutMs && timeoutMs > 0) {
      xhr.timeout = timeoutMs;
    }
    Object.entries(headers || {}).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    xhr.upload.onprogress = (event) => {
      // iOS Safari and WKWebView can omit upload progress events. Only start
      // the no-progress watchdog after the first event; xhr.timeout is the
      // backstop when progress never fires.
      armStall();
      if (!event.lengthComputable || !event.total) return;
      onProgress?.(Math.max(1, Math.min(99, Math.round((event.loaded / event.total) * 100))));
    };
    xhr.onload = () => {
      settle(() => resolve(xhr));
    };
    xhr.onerror = () => {
      settle(() => reject(new Error(STREAM_VIDEO_ERRORS.uploadFailed)));
    };
    xhr.onabort = () => {
      settle(() => reject(new Error(STREAM_VIDEO_ERRORS.cancelled)));
    };
    xhr.ontimeout = failStalled;
    xhr.send(body ?? null);
  });
}

function postFileToUploadUrl(
  uploadURL: string,
  file: File,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal
): Promise<void> {
  return xhrSend({
    method: 'POST',
    url: uploadURL,
    body: file,
    onProgress,
    signal,
  }).then((xhr) => {
    if (xhr.status >= 200 && xhr.status < 300) {
      onProgress?.(100);
      return;
    }
    throw new Error(STREAM_VIDEO_ERRORS.uploadFailed);
  });
}

async function readTusOffset(uploadURL: string, signal?: AbortSignal): Promise<number> {
  const response = await fetch(uploadURL, {
    method: 'HEAD',
    headers: { 'Tus-Resumable': '1.0.0' },
    signal,
  });
  if (!response.ok) return 0;
  const offset = Number(response.headers.get('Upload-Offset'));
  return Number.isFinite(offset) && offset >= 0 ? offset : 0;
}

async function tusPatchUpload(
  uploadURL: string,
  file: File,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal
): Promise<void> {
  let offset = await readTusOffset(uploadURL, signal);
  while (offset < file.size) {
    throwIfAborted(signal);
    let lastError: Error | null = null;
    let succeeded = false;
    for (let attempt = 0; attempt <= TUS_PATCH_RETRIES; attempt += 1) {
      throwIfAborted(signal);
      const end = Math.min(offset + STREAM_TUS_CHUNK_BYTES, file.size);
      const chunk = file.slice(offset, end);
      try {
        const chunkSize = end - offset;
        const xhr = await xhrSend({
          method: 'PATCH',
          url: uploadURL,
          headers: {
            'Tus-Resumable': '1.0.0',
            'Upload-Offset': String(offset),
            'Content-Type': 'application/offset+octet-stream',
          },
          body: chunk,
          onProgress: (chunkPercent) => {
            const loaded = offset + Math.round((chunkPercent / 100) * chunkSize);
            onProgress?.(Math.max(1, Math.min(99, Math.round((loaded / file.size) * 100))));
          },
          signal,
        });
        if (xhr.status < 200 || xhr.status >= 300) {
          throw new Error(STREAM_VIDEO_ERRORS.uploadFailed);
        }
        const reported = Number(xhr.getResponseHeader('Upload-Offset'));
        offset = Number.isFinite(reported) && reported > offset ? reported : end;
        succeeded = true;
        lastError = null;
        break;
      } catch (error) {
        if (signal?.aborted || isAbortError(error)) {
          throw new Error(STREAM_VIDEO_ERRORS.cancelled);
        }
        lastError = error instanceof Error ? error : new Error(STREAM_VIDEO_ERRORS.uploadFailed);
        offset = await readTusOffset(uploadURL, signal);
      }
    }
    if (!succeeded) {
      throw lastError || new Error(STREAM_VIDEO_ERRORS.uploadFailed);
    }
    onProgress?.(Math.max(1, Math.min(99, Math.round((offset / file.size) * 100))));
  }
  onProgress?.(100);
}

export async function uploadStreamVideo(
  file: File,
  options: UploadStreamVideoOptions = {}
): Promise<{ uid: string; iframeSrc: string; durationSeconds: number }> {
  const token = options.token ?? localStorage.getItem('token');
  if (!token) {
    throw new Error(STREAM_VIDEO_ERRORS.notSignedIn);
  }

  const fileError = getStreamVideoFileError(file);
  if (fileError) {
    throw new Error(fileError);
  }

  throwIfAborted(options.signal);
  if (!(await hasMp4FtypBox(file))) {
    throw new Error(STREAM_VIDEO_ERRORS.notMp4);
  }

  throwIfAborted(options.signal);
  let duration: number;
  let fileSize: { width: number; height: number } | null = null;
  if (options.readDuration) {
    duration = await options.readDuration(file);
    if (options.readFileSize) {
      fileSize = await options.readFileSize(file);
    }
  } else {
    const meta = await readVideoFileMeta(file);
    duration = meta.duration;
    fileSize = normalizeStreamSize(meta.width, meta.height);
  }
  const durationError = getStreamVideoDurationError(duration);
  if (durationError) {
    throw new Error(durationError);
  }

  throwIfAborted(options.signal);
  const protocol = chooseStreamUploadProtocol(file.size);
  const minted = await mintStreamUploadUrl({
    token,
    size: file.size,
    filename: file.name,
    contentType: getStreamVideoContentType(file),
    protocol,
    signal: options.signal,
  });
  options.onMinted?.(minted.uid);

  switch (minted.protocol) {
    case 'post':
      await postFileToUploadUrl(minted.uploadURL, file, options.onProgress, options.signal);
      break;
    case 'tus':
      await tusPatchUpload(minted.uploadURL, file, options.onProgress, options.signal);
      break;
    default:
      assertNever(minted.protocol, `Unsupported Stream upload protocol: ${String(minted.protocol)}`);
  }

  const processing: StreamVideoStatus = {
    uid: minted.uid,
    ready: false,
    state: 'queued',
    iframeSrc: minted.iframeSrc,
  };
  options.onProcessing?.(processing);

  const ready = await waitForStreamVideoReady({
    uid: minted.uid,
    token,
    signal: options.signal,
    onStatus: options.onProcessing,
    pollMs: options.readyPollMs,
    pollMaxMs: options.readyPollMaxMs,
    maxAttempts: options.readyMaxAttempts,
    timeoutMs: options.readyTimeoutMs,
  });

  return {
    uid: ready.uid,
    iframeSrc: withStreamAspect(
      ready.iframeSrc,
      ready.width || fileSize?.width,
      ready.height || fileSize?.height
    ),
    durationSeconds: duration,
  };
}
